import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";
import SEOHead from "../../components/base/SEOHead";
import { liveChannels, broadcastSchedule } from "../../mocks/liveTV";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://xn--9z2b23f95curk71m6qd.com";

const liveTVJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "실시간 무료스포츠중계 채널",
  description: "모든 스포츠 중계 채널을 실시간으로 확인하고 시청하세요. 축구, 야구, 농구, 배구, e스포츠까지 모든 종목을 생중계합니다.",
  numberOfItems: liveChannels.length,
  itemListElement: liveChannels.map((ch, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "BroadcastChannel",
      name: ch.name,
      description: `${ch.category} 전문 중계 채널`,
      broadcastServiceTier: "Standard",
    },
  })),
};

export default function LiveTV() {
  return (
    <div className="min-h-screen bg-dark-bg">
      <SEOHead
        title="실시간 무료스포츠중계 | 모든 종목 생중계 채널 - 할매티비"
        description="실시간 무료스포츠중계 채널을 통해 회원가입 없이 바로 접속하여 시청하세요. 축구, 야구, 농구, 배구, e스포츠까지 8개 실시간 중계 채널로 모든 종목을 생중계합니다."
        keywords="실시간무료스포츠중계,스포츠중계채널,생중계채널,축구중계채널,야구중계채널,무료중계"
        canonicalPath="/live-tv"
        jsonLd={liveTVJsonLd}
      />
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-white font-bold text-2xl md:text-3xl mb-2">
              실시간 무료스포츠중계
            </h1>
            <p className="text-dark-text-muted text-base">
              모든 스포츠 중계 채널을 실시간으로 확인하고 시청하세요.
            </p>
          </div>

          {/* Hero Banner */}
          <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8 mb-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-brand-orange/10 rounded-full border border-brand-orange/30">
              <i className="ri-tv-line text-brand-orange text-3xl"></i>
            </div>
            <h2 className="text-white font-bold text-xl md:text-2xl mb-2">
              8개 실시간 중계 채널
            </h2>
            <p className="text-dark-text-muted text-sm max-w-lg mx-auto mb-6">
              축구, 야구, 농구, 배구, e스포츠까지 모든 종목을 실시간으로 생중계합니다.
            </p>
            <Link
              to="/sports-schedule"
              className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-base py-4 px-10 rounded-full transition-all whitespace-nowrap"
            >
              <i className="ri-tv-line text-xl"></i>
              <span>채널 선택하기</span>
              <i className="ri-arrow-right-up-line text-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"></i>
            </Link>
          </div>

          {/* Live Channels */}
          <div className="mb-12">
            <h2 className="text-white font-bold text-xl mb-6">실시간 채널 현황</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {liveChannels.map((ch) => (
                <div
                  key={ch.id}
                  className="bg-dark-surface border border-dark-border rounded-xl p-4 md:p-5 hover:border-brand-orange transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-brand-orange/10 rounded-lg">
                        <i className="ri-tv-line text-brand-orange text-lg"></i>
                      </div>
                      <div>
                        <span className="text-white font-bold text-sm block">{ch.name}</span>
                        <span className="text-dark-text-muted text-xs">{ch.category}</span>
                      </div>
                    </div>
                    {ch.current !== "휴식 중" && (
                      <span className="flex items-center gap-1 text-red-500 text-xs font-bold bg-red-500/10 px-2 py-1 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                        ON AIR
                      </span>
                    )}
                    {ch.current === "휴식 중" && (
                      <span className="text-dark-text-muted text-xs font-bold bg-dark-border px-2 py-1 rounded-full">
                        휴식
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-brand-orange text-xs font-bold">NOW</span>
                      <span className="text-white text-sm">{ch.current}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-dark-text-muted text-xs">NEXT</span>
                      <span className="text-dark-text-muted text-sm">{ch.next}</span>
                    </div>
                  </div>
                  {ch.viewers !== "-" && (
                    <div className="mt-3 pt-3 border-t border-dark-border flex items-center gap-2">
                      <i className="ri-user-line text-dark-text-muted text-xs"></i>
                      <span className="text-dark-text-muted text-xs">{ch.viewers}명 시청 중</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Broadcast Schedule */}
          <div className="mb-12">
            <h2 className="text-white font-bold text-xl mb-6">오늘의 방송 일정</h2>
            <div className="space-y-3">
              {broadcastSchedule.map((item, i) => (
                <div
                  key={i}
                  className="bg-dark-surface border border-dark-border rounded-xl p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:border-brand-orange transition-all"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-brand-orange font-bold text-sm w-14">{item.time}</span>
                    <div>
                      <span className="text-white font-bold text-sm block">{item.program}</span>
                      <span className="text-dark-text-muted text-xs">{item.channel}</span>
                    </div>
                  </div>
                  {item.status === "live" ? (
                    <span className="flex items-center gap-1 text-red-500 text-xs font-bold bg-red-500/10 px-3 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                      생중계
                    </span>
                  ) : (
                    <span className="text-brand-orange text-xs font-bold bg-brand-orange/10 px-3 py-1 rounded-full">
                      예정
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* SEO Content */}
          <div className="border-t border-dark-border pt-8">
            <h2 className="text-white font-bold text-xl mb-4">실시간 무료스포츠중계 안내</h2>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">실시간 무료스포츠중계</strong>는 회원가입 없이 바로 접속하여 시청할 수 있는 서비스입니다. 할매티비는 모든 주요 스포츠 경기를 실시간으로 생중계하며, 다양한 채널을 통해 원하는 종목을 선택하여 시청할 수 있습니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">실시간 무료스포츠중계</strong> 채널은 축구, 야구, 농구, 배구, e스포츠 등 다양한 종목을 전문적으로 중계합니다. 각 채널은 현재 생중계 중인 경기와 다음 예정 경기를 실시간으로 업데이트하여 제공합니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed">
              <strong className="text-white">스포츠중계</strong>를 실시간으로 시청하려면 할매티비의 실시간 무료스포츠중계를 이용하세요. PC와 모바일에서 모두 최적화된 시청 환경을 제공하며, 언제 어디서나 최고의 스포츠 경기를 생생하게 즐길 수 있습니다.
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}